import React, { useState } from "react";
import {
  Save,
  Edit3,
  Plus,
  Settings,
  Trash2,
  X,
  Check,
  Wallet,
  DollarSign,
  CalendarDays,
  TriangleAlert,
  CheckCircle2,
  Loader2,
  Clock,
} from "lucide-react";
import Card from "./common/Card";
import StatCard from "./common/StatCard";
import SafeChart from "./common/SafeChart";
import GlowBars from "./charts/GlowBars";
import GlowDonut from "./charts/GlowDonut";
import {
  hasUnsavedIncomeChanges,
  stableStringify,
} from "./income/incomeWriteGuard";

const EditableInput = ({ value, onChange, className }) => (
  <input
    type="number"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`border border-arc-line rounded-arc-sm bg-transparent text-arc-ink px-2 py-1 tnum focus:outline-none focus:ring-2 focus:ring-arc-accent ${className}`}
  />
);

const IncomeTab = ({
  incomeData = [],
  incomeColumns = ["salary", "business", "rental", "crypto", "affiliate"],
  editMode,
  onToggleEdit,
  updateIncomeData,
  onUpdateColumns,
  chartsReady = true,
  addNextMonth,
  setIncomeData, // 👈 required for flexible month insertion
  /* ─── ARC-58: committed (server-confirmed) state, explicit save, fail-closed
     reads. All optional — without them the tab behaves exactly as it did
     before, but the shell (FinancialDashboard) supplies them, and only then can
     this tab tell "saved" apart from "typed". */
  committedIncomeData,
  committedIncomeColumns,
  onSaveIncome,      // async () => { success, error } — performs a real save
  hasUnsavedChanges, // optional override of the local dirty check
  loadError,         // non-empty when the income read failed: tab goes read-only
}) => {
  const [showColumnEditor, setShowColumnEditor] = useState(false);
  const [showMonthInsertModal, setShowMonthInsertModal] = useState(false);
  const [tempColumns, setTempColumns] = useState([]);
  const [newColumnName, setNewColumnName] = useState("");

  /* ─── ARC-58: fail closed ──────────────────────────────────────────────
     A failed read tells us nothing about what the server holds. Every path
     that could write is shut off — not just the Save button, but editing,
     adding months, changing columns and deleting rows, so there is nothing to
     lose by refusing to save. */
  const readOnly = Boolean(loadError);
  const canEdit = editMode && !readOnly;

  /* ─── ARC-58: committed state ──────────────────────────────────────────
     The table is the edit buffer. Everything derived from it below — the three
     stat cards and both charts — reads the last SERVER-CONFIRMED state, so a
     member is never shown a figure that was not persisted. Falls back to the
     buffer when the shell passes no committed snapshot (pre-ARC-58 behaviour);
     after a failed read there is no snapshot and no buffer to fall back to. */
  const hasCommitted = Array.isArray(committedIncomeData);
  const committedData = hasCommitted ? committedIncomeData : incomeData;
  const committedColumns =
    Array.isArray(committedIncomeColumns) && committedIncomeColumns.length
      ? committedIncomeColumns
      : incomeColumns;

  const dirty =
    typeof hasUnsavedChanges === "boolean"
      ? hasUnsavedChanges
      : hasCommitted &&
        hasUnsavedIncomeChanges(
          { incomeColumns, incomeData },
          { incomeColumns: committedColumns, incomeData: committedIncomeData }
        );

  /* ─── ARC-58: explicit save result ─────────────────────────────────────
     { kind: "saving" | "success" | "error", text, signature }. Leaving edit
     mode is not evidence of persistence; every save resolves to one of these. */
  const [saveStatus, setSaveStatus] = useState(null);
  const saving = saveStatus?.kind === "saving";
  // A "saved" notice describes the buffer as it was when it was written, so it
  // stands only while the buffer is unchanged since that write.
  const bufferSignature = stableStringify({ incomeColumns, incomeData });
  const savedNoticeStillTrue =
    !dirty || saveStatus?.signature === bufferSignature;
  const visibleStatus =
    saveStatus?.kind === "success" && !savedNoticeStillTrue ? null : saveStatus;

  const handleEditSaveClick = async () => {
    if (readOnly || saving) return;

    if (!editMode) {
      setSaveStatus(null);
      if (onToggleEdit) onToggleEdit();
      return;
    }

    // No save handler wired — keep the old toggle, and claim nothing was saved.
    if (typeof onSaveIncome !== "function") {
      if (onToggleEdit) onToggleEdit();
      return;
    }

    setSaveStatus({ kind: "saving", text: "Saving your changes…" });

    let result;
    try {
      result = await onSaveIncome();
    } catch (e) {
      result = { success: false, error: e?.message };
    }

    if (result && result.success) {
      setSaveStatus({
        kind: "success",
        text: "Changes saved. The figures below come from your saved data.",
        signature: stableStringify({ incomeColumns, incomeData }),
      });
      if (onToggleEdit) onToggleEdit();
      return;
    }

    /* Failed: stay in edit mode so every entered value stays on screen and
       editable. Some upstream errors already promise the entries survived;
       adding our own to those would print that promise twice. */
    const reassures = (msg) => /still here|still on screen/i.test(msg);
    setSaveStatus({
      kind: "error",
      text: result?.error
        ? reassures(result.error)
          ? `Not saved: ${result.error}`
          : `Not saved: ${result.error} Your entries are still here — try Save again.`
        : "Not saved. Your entries are still here — check your connection and try Save again.",
    });
  };

  // For month insertion
  const [newMonthName, setNewMonthName] = useState("");
  const [insertPosition, setInsertPosition] = useState("end");
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);

  const openColumnEditor = () => {
    setTempColumns([...incomeColumns]);
    setShowColumnEditor(true);
  };

  const addColumn = () => {
    if (newColumnName.trim() && !tempColumns.includes(newColumnName.trim().toLowerCase())) {
      setTempColumns([...tempColumns, newColumnName.trim().toLowerCase()]);
      setNewColumnName("");
    }
  };

  const removeColumn = (columnToRemove) => {
    setTempColumns(tempColumns.filter((col) => col !== columnToRemove));
  };

  const saveColumns = () => {
    if (tempColumns.length > 0) onUpdateColumns(tempColumns);
    setShowColumnEditor(false);
  };

  const cancelColumnEdit = () => {
    setTempColumns([]);
    setNewColumnName("");
    setShowColumnEditor(false);
  };

  const formatColumnName = (columnName) =>
    columnName.charAt(0).toUpperCase() + columnName.slice(1);

  /* ─── Month Insertion Logic ──────────────────────────────────────────── */
  const openMonthInsertModal = () => {
    setNewMonthName("");
    setInsertPosition("end");
    setSelectedMonthIndex(0);
    setShowMonthInsertModal(true);
  };

  const handleInsertMonth = () => {
    if (!newMonthName.trim()) {
      alert("Please enter a month name");
      return;
    }

    // Create new month object with all columns initialized to 0
    const newMonth = { month: newMonthName.trim(), total: 0 };
    incomeColumns.forEach((col) => {
      newMonth[col] = 0;
    });

    const updatedData = [...incomeData];

    if (insertPosition === "beginning") {
      updatedData.unshift(newMonth);
    } else if (insertPosition === "end") {
      updatedData.push(newMonth);
    } else if (insertPosition === "before") {
      updatedData.splice(selectedMonthIndex, 0, newMonth);
    } else if (insertPosition === "after") {
      updatedData.splice(selectedMonthIndex + 1, 0, newMonth);
    }

    setIncomeData(updatedData);
    setShowMonthInsertModal(false);
  };

  /* ─── Delete Month Handler ───────────────────────────────────────────── */
  const deleteMonth = (monthIndex) => {
    if (window.confirm("Are you sure you want to delete this month?")) {
      const updated = incomeData.filter((_, idx) => idx !== monthIndex);
      setIncomeData(updated);
    }
  };

  /* ─── Current income mix (latest month, by source) for the donut ─────────
     Presentation-only projection of the existing data — no calculation change.
     The full per-source, per-month breakdown stays in the editable table above.
     ARC-58: reads committed state, like every other widget below. */
  const latestMonth = committedData[committedData.length - 1];
  const incomeMix = latestMonth
    ? committedColumns
        .map((col) => ({ name: formatColumnName(col), value: latestMonth[col] || 0 }))
        .filter((d) => d.value > 0)
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h3 className="text-h2 text-arc-ink">Income Tracking</h3>

        <div className="flex gap-2">
          <button
            onClick={openMonthInsertModal}
            disabled={readOnly}
            className="flex items-center gap-2 rounded-arc-ctl px-4 py-2.5 bg-arc-card text-arc-ink font-semibold ring-1 ring-arc-line shadow-arc-sm transition hover:bg-arc-paper-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Plus size={16} /> Add Month
          </button>

          <button
            onClick={openColumnEditor}
            disabled={readOnly}
            className="flex items-center gap-2 rounded-arc-ctl px-4 py-2.5 bg-arc-card text-arc-ink font-semibold ring-1 ring-arc-line shadow-arc-sm transition hover:bg-arc-paper-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Settings size={16} /> Columns
          </button>

          <button
            onClick={handleEditSaveClick}
            disabled={readOnly || saving}
            aria-busy={saving}
            className={`flex items-center gap-2 rounded-arc-ctl px-4 py-2.5 text-white font-semibold shadow-arc-sm transition hover:brightness-105 active:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed ${
              editMode ? "bg-arc-pos" : "bg-arc-accent"
            }`}
          >
            {saving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : editMode ? (
              <Save size={16} />
            ) : (
              <Edit3 size={16} />
            )}
            {saving ? "Saving…" : editMode ? "Save" : "Edit"}
          </button>
        </div>
      </div>

      {/* ─── ARC-58: failed read ───────────────────────────────────────────
          The tab is read-only and saving is disabled entirely. Writing an
          empty or default table on top of data we failed to read is the
          overwrite this issue exists to prevent. */}
      {readOnly && (
        <div
          role="alert"
          className="flex gap-3 p-4 rounded-arc-card shadow-arc-sm ring-1 bg-arc-neg-soft ring-arc-neg-soft text-arc-neg"
        >
          <TriangleAlert size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-[14.5px] leading-[1.55]">{loadError}</div>
        </div>
      )}

      {/* ─── ARC-58: explicit save outcome ─────────────────────────────────
          Leaving edit mode is not confirmation. Every save resolves here to a
          visible saving / saved / not-saved state. On failure the tab stays in
          edit mode and the entered values stay in the buffer. */}
      {visibleStatus && (
        <div
          role={visibleStatus.kind === "error" ? "alert" : "status"}
          aria-live={visibleStatus.kind === "error" ? "assertive" : "polite"}
          className={`flex gap-3 p-4 rounded-arc-card shadow-arc-sm ring-1 ${
            visibleStatus.kind === "error"
              ? "bg-arc-neg-soft ring-arc-neg-soft text-arc-neg"
              : visibleStatus.kind === "success"
              ? "bg-arc-pos-soft ring-arc-pos-soft text-arc-pos"
              : "bg-arc-paper-2 ring-arc-line text-arc-ink-2"
          }`}
        >
          {visibleStatus.kind === "error" ? (
            <TriangleAlert size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          ) : visibleStatus.kind === "success" ? (
            <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          ) : (
            <Loader2 size={18} className="flex-shrink-0 mt-0.5 animate-spin" aria-hidden="true" />
          )}
          <div className="text-[14.5px] leading-[1.55]">{visibleStatus.text}</div>
        </div>
      )}

      {/* Month Insert Modal */}
      {showMonthInsertModal && (
        <div className="fixed inset-0 bg-[rgba(10,37,64,.32)] backdrop-blur-[5px] flex items-center justify-center z-50">
          <div className="bg-arc-card rounded-arc-card shadow-arc-lg ring-1 ring-arc-line p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-h2 text-arc-ink">Insert New Month</h4>
              <button
                onClick={() => setShowMonthInsertModal(false)}
                aria-label="Close"
                className="text-arc-ink-3 hover:text-arc-ink"
              >
                <X size={20} />
              </button>
            </div>

            {/* Month Name Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-arc-ink-2 mb-2">
                Month Name (e.g., "Jan 2025", "February 2025")
              </label>
              <input
                type="text"
                value={newMonthName}
                onChange={(e) => setNewMonthName(e.target.value)}
                placeholder="Enter month name..."
                className="w-full px-3 py-2 bg-transparent text-arc-ink border border-arc-line rounded-arc-ctl focus:outline-none focus:ring-2 focus:ring-arc-accent"
                autoFocus
              />
            </div>

            {/* Position Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-arc-ink-2 mb-2">
                Insert Position
              </label>
              <select
                value={insertPosition}
                onChange={(e) => setInsertPosition(e.target.value)}
                className="w-full px-3 py-2 bg-arc-card text-arc-ink border border-arc-line rounded-arc-ctl focus:outline-none focus:ring-2 focus:ring-arc-accent"
              >
                <option value="beginning">At the beginning</option>
                <option value="end">At the end</option>
                {incomeData.length > 0 && (
                  <>
                    <option value="before">Before a specific month</option>
                    <option value="after">After a specific month</option>
                  </>
                )}
              </select>
            </div>

            {/* Specific Month Selection (if before/after) */}
            {(insertPosition === "before" || insertPosition === "after") && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-arc-ink-2 mb-2">
                  Select Month
                </label>
                <select
                  value={selectedMonthIndex}
                  onChange={(e) => setSelectedMonthIndex(parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-arc-card text-arc-ink border border-arc-line rounded-arc-ctl focus:outline-none focus:ring-2 focus:ring-arc-accent"
                >
                  {incomeData.map((row, idx) => (
                    <option key={idx} value={idx}>
                      {row.month}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowMonthInsertModal(false)}
                className="rounded-arc-ctl px-4 py-2.5 bg-arc-card text-arc-ink font-semibold ring-1 ring-arc-line transition hover:bg-arc-paper-2"
              >
                Cancel
              </button>
              <button
                onClick={handleInsertMonth}
                className="flex items-center gap-2 rounded-arc-ctl px-4 py-2.5 bg-arc-pos text-white font-semibold shadow-arc-sm transition hover:brightness-105 active:brightness-95"
              >
                <Check size={16} /> Insert Month
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Editor Modal */}
      {showColumnEditor && (
        <div className="fixed inset-0 bg-[rgba(10,37,64,.32)] backdrop-blur-[5px] flex items-center justify-center z-50">
          <div className="bg-arc-card rounded-arc-card shadow-arc-lg ring-1 ring-arc-line p-6 max-w-md w-full mx-4">
            <h4 className="text-h2 text-arc-ink mb-4">Manage Income Columns</h4>

            <div className="mb-4">
              <label className="block text-sm font-medium text-arc-ink-2 mb-2">Current Columns:</label>
              <div className="space-y-2">
                {tempColumns.map((column) => (
                  <div
                    key={column}
                    className="flex items-center justify-between bg-arc-paper-2 p-2 rounded-arc-sm"
                  >
                    <span className="text-arc-ink">{formatColumnName(column)}</span>
                    <button
                      onClick={() => removeColumn(column)}
                      className="text-arc-neg hover:brightness-110 disabled:opacity-50"
                      disabled={tempColumns.length <= 1}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-arc-ink-2 mb-2">Add New Column:</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="Column name..."
                  className="flex-1 bg-transparent text-arc-ink border border-arc-line rounded-arc-ctl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-arc-accent"
                  onKeyPress={(e) => e.key === "Enter" && addColumn()}
                />
                <button
                  onClick={addColumn}
                  disabled={!newColumnName.trim()}
                  className="rounded-arc-ctl px-3 py-2 bg-arc-accent text-white font-semibold shadow-arc-sm transition hover:brightness-105 disabled:opacity-50"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={cancelColumnEdit}
                className="rounded-arc-ctl px-4 py-2.5 bg-arc-card text-arc-ink font-semibold ring-1 ring-arc-line transition hover:bg-arc-paper-2"
              >
                Cancel
              </button>
              <button
                onClick={saveColumns}
                className="rounded-arc-ctl px-4 py-2.5 bg-arc-pos text-white font-semibold shadow-arc-sm transition hover:brightness-105 active:brightness-95"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editable Table */}
      <Card hover={false}>
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.03em] text-arc-ink-4 border-b border-arc-line-2">
                  Month
                </th>
                {incomeColumns.map((column) => (
                  <th
                    key={column}
                    className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.03em] text-arc-ink-4 border-b border-arc-line-2"
                  >
                    {formatColumnName(column)}
                  </th>
                ))}
                <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.03em] text-arc-ink-4 border-b border-arc-line-2">
                  Total
                </th>
                <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.03em] text-arc-ink-4 border-b border-arc-line-2">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {incomeData.map((row, idx) => (
                <tr key={`${row.month}-${idx}`} className="transition-colors hover:bg-arc-paper">
                  <td className="px-3 py-2.5 border-b border-arc-line font-medium text-arc-ink">
                    {row.month}
                  </td>

                  {incomeColumns.map((column) => (
                    <td key={column} className="px-3 py-2.5 border-b border-arc-line text-right tnum text-arc-ink-2">
                      {canEdit ? (
                        <EditableInput
                          value={row[column] || 0}
                          onChange={(val) => updateIncomeData(idx, column, val)}
                          className="w-20 text-right"
                        />
                      ) : (
                        (row[column] || 0).toLocaleString()
                      )}
                    </td>
                  ))}

                  <td className="px-3 py-2.5 border-b border-arc-line text-right tnum font-semibold text-arc-pos">
                    ${(row.total || 0).toLocaleString()}
                  </td>

                  {/* Delete Month button */}
                  <td className="px-3 py-2.5 border-b border-arc-line text-center">
                    <button
                      onClick={() => deleteMonth(idx)}
                      disabled={readOnly}
                      className="text-arc-neg hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed"
                      title="Delete Month"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}

              {/* ARC-58: income no longer starts from a demo table, so an
                  account that has entered nothing yet lands here. */}
              {incomeData.length === 0 && (
                <tr>
                  <td
                    colSpan={incomeColumns.length + 3}
                    className="px-3 py-8 text-center text-sm text-arc-ink-3"
                  >
                    {readOnly
                      ? "Your income data could not be loaded."
                      : 'No income months yet. Click "Add Month" to start tracking.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ARC-58: pending-edits marker. Says plainly that the figures below are
          the saved ones, so nothing here can be mistaken for a confirmed save. */}
      {dirty && committedData.length > 0 && (
        <div
          role="status"
          className="flex items-center gap-2 px-3 py-2 rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 text-arc-ink-2 text-[12.5px]"
        >
          <Clock size={14} className="flex-shrink-0 text-arc-ink-3" aria-hidden="true" />
          <span>
            Unsaved edits in the table. The figures and charts below show your last
            saved data until you save.
          </span>
        </div>
      )}

      {/* Average + total summary — ARC-58: from committed (saved) state only. */}
      {committedData.length > 0 && (() => {
        const total = committedData.reduce((s, r) => s + (r.total || 0), 0);
        const months = committedData.length;
        const avg = months > 0 ? total / months : 0;
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              title="Avg Monthly Income"
              value={avg}
              icon={Wallet}
              subtitle={`across ${months} months tracked`}
            />
            <StatCard
              title="Total Income"
              value={total}
              icon={DollarSign}
              subtitle="all months combined"
            />
            <StatCard
              title="Latest Month"
              value={committedData[committedData.length - 1]?.total || 0}
              icon={CalendarDays}
              subtitle={committedData[committedData.length - 1]?.month || "—"}
            />
          </div>
        );
      })()}

      {/* Charts — total-per-month GlowBars (gold, tab accent) + current-mix GlowDonut (NEON) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card hover={false} className="lg:col-span-2">
          <h3 className="text-h2 text-arc-ink mb-4">Monthly Income</h3>
          <SafeChart chartsReady={chartsReady}>
            <GlowBars
              data={committedData}
              dataKey="total"
              xKey="month"
              height={360}
              yTickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
            />
          </SafeChart>
        </Card>

        <Card hover={false}>
          <h3 className="text-h2 text-arc-ink mb-4">Income Mix by Source</h3>
          {incomeMix.length > 0 ? (
            <SafeChart chartsReady={chartsReady}>
              <GlowDonut
                data={incomeMix}
                dataKey="value"
                nameKey="name"
                height={360}
                valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
              />
            </SafeChart>
          ) : (
            <div className="flex items-center justify-center h-[360px] text-arc-ink-3 text-sm">
              No income recorded for the latest month yet.
            </div>
          )}
          {latestMonth && incomeMix.length > 0 && (
            <p className="mt-2 text-center text-[11.5px] text-arc-ink-3">
              Current mix — {latestMonth.month}
            </p>
          )}
        </Card>
      </div>

      {/* Info */}
      <div className="text-sm text-arc-ink-2">
        <p>
          Click "Add Month" to insert a month at any position in your income tracking.
        </p>
      </div>
    </div>
  );
};

export default IncomeTab;
